import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, X, Loader2, Activity, TrendingUp, Layers } from "lucide-react";
import { searchTickers, getQuote, getEtfInfo, computeTotalReturn, createWatchlist } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import TickerHeader from "@/components/tracker/TickerHeader";
import StockSummaryCard from "@/components/tracker/StockSummaryCard";
import DividendInfoCard from "@/components/tracker/DividendInfoCard";
import EtfFundamentalsCard from "@/components/tracker/EtfFundamentalsCard";
import EtfHoldingsCard from "@/components/tracker/EtfHoldingsCard";
import EtfsByTypeDrawer from "@/components/tracker/EtfsByTypeDrawer";
import HeldByEtfsCard from "@/components/tracker/HeldByEtfsCard";
import PeerEtfsStrip from "@/components/tracker/PeerEtfsStrip";
import IntervalSelector from "@/components/tracker/IntervalSelector";
import ResultsPanel from "@/components/tracker/ResultsPanel";
import CompareView from "@/components/tracker/CompareView";
import WatchlistsPanel from "@/components/tracker/WatchlistsPanel";

const DEFAULT_INTERVALS = ["since_2020", "ytd"];
const PIN_LS_KEY = "trt_pinned_v1";
const MAX_PIN = 4;

function useDebounce(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function TotalReturnTracker() {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const debounced = useDebounce(query, 250);

  const [activeSymbol, setActiveSymbol] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [etf, setEtf] = useState(null);
  const [loadingEtf, setLoadingEtf] = useState(false);
  const [etfYtdReturn, setEtfYtdReturn] = useState(null);

  const [intervals, setIntervals] = useState(DEFAULT_INTERVALS);
  const [results, setResults] = useState(null);
  const [computing, setComputing] = useState(false);
  const [mode, setMode] = useState("simple"); // "simple" | "reinvest"
  const [drawerTypeId, setDrawerTypeId] = useState(null); // open EtfsByTypeDrawer when set

  // Compare state: array of { symbol, name, results }
  const [pinned, setPinned] = useState(() => {
    try {
      const raw = localStorage.getItem(PIN_LS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  // Persist pinned to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(PIN_LS_KEY, JSON.stringify(pinned));
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[tracker] failed to persist pinned tickers:", err);
      }
    }
  }, [pinned]);

  const { user } = useAuth();

  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const searchAbortRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSugg(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Deep-link: support /?t=SYMBOL (used by PublicWatchlist "Held by ETFs" links).
  // Runs once on mount, then strips the param so refresh doesn't reload.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = (params.get("t") || params.get("ticker") || "").toUpperCase().trim();
      if (t && /^[A-Z0-9.\-]{1,8}$/.test(t)) {
        loadSymbol(t);
        params.delete("t");
        params.delete("ticker");
        const qs = params.toString();
        const newUrl = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
        window.history.replaceState({}, "", newUrl);
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const q = debounced.trim();
    if (q.length === 0) {
      setSuggestions([]);
      setSearchError(null);
      setSearching(false);
      return;
    }
    if (activeSymbol && quote && q === `${quote.symbol} — ${quote.name}`) {
      setSuggestions([]);
      setSearchError(null);
      setSearching(false);
      return;
    }
    // Abort any in-flight request from previous keystroke
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    let cancelled = false;
    setSearching(true);
    setSearchError(null);
    searchTickers(q, { signal: controller.signal })
      .then((data) => {
        if (cancelled || controller.signal.aborted) return;
        setSuggestions(data.results || []);
      })
      .catch((err) => {
        if (cancelled || controller.signal.aborted) return;
        // Distinguish "request failed" (5xx/network) from "no matches" (200 empty)
        const status = err?.response?.status;
        const isNetwork = !err?.response;
        if (isNetwork || (status && status >= 500)) {
          setSuggestions([]);
          setSearchError("Search service is temporarily unavailable. Please retry.");
          toast.error("Search service is temporarily unavailable. Please retry.");
        } else {
          setSuggestions([]);
        }
      })
      .finally(() => {
        // Always clear the spinner — even when this fetch was aborted by a newer
        // keystroke, the unmount/cleanup, or a manual loadSymbol() call.
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debounced, activeSymbol, quote]);

  const loadSymbol = async (sym) => {
    if (!sym) return;
    // Abort any in-flight search when a ticker is being loaded
    if (searchAbortRef.current) searchAbortRef.current.abort();
    setShowSugg(false);
    setSearching(false);
    setActiveSymbol(sym);
    setQuote(null);
    setEtf(null);
    setEtfYtdReturn(null);
    setResults(null);
    setLoadingQuote(true);
    setLoadingEtf(true);
    try {
      const data = await getQuote(sym);
      setQuote(data);
      setQuery(`${data.symbol} — ${data.name}`);
      // ETF info fetched in parallel with quote-success path (non-blocking for compute)
      getEtfInfo(sym)
        .then((info) => {
          setEtf(info);
          // If the symbol is an ETF, also kick off a YTD-only compute for the card
          // (DB-served so essentially free). Independent of user's interval checks.
          if (info?.is_etf) {
            computeTotalReturn(sym, ["ytd"])
              .then((tr) => {
                const ytd = (tr.results || []).find((r) => r.interval_id === "ytd");
                setEtfYtdReturn(ytd?.total_return_pct ?? null);
              })
              .catch(() => setEtfYtdReturn(null));
          }
        })
        .catch(() => setEtf(null))
        .finally(() => setLoadingEtf(false));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to load symbol";
      toast.error(typeof msg === "string" ? msg : "Failed to load symbol");
      setActiveSymbol(null);
      setLoadingEtf(false);
    } finally {
      setLoadingQuote(false);
    }
  };

  const onCompute = async () => {
    if (!activeSymbol) {
      toast.error("Search for a ticker first");
      return;
    }
    if (intervals.length === 0) {
      toast.error("Select at least 1 interval");
      return;
    }
    setComputing(true);
    setResults(null);
    try {
      const data = await computeTotalReturn(activeSymbol, intervals);
      setResults(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Failed to compute";
      toast.error(typeof msg === "string" ? msg : "Failed to compute");
    } finally {
      setComputing(false);
    }
  };

  const isPinned = quote && pinned.some((p) => p.symbol === quote.symbol);

  const togglePin = async () => {
    if (!quote) return;
    if (isPinned) {
      setPinned((p) => p.filter((x) => x.symbol !== quote.symbol));
      return;
    }
    if (pinned.length >= MAX_PIN) {
      toast.error(`Max ${MAX_PIN} tickers in compare`);
      return;
    }
    if (intervals.length === 0) {
      toast.error("Select intervals first");
      return;
    }
    try {
      const data = await computeTotalReturn(quote.symbol, intervals);
      setPinned((p) => [...p, { symbol: data.symbol, name: data.name, results: data.results }]);
      toast.success(`${quote.symbol} pinned to compare`);
    } catch (e) {
      toast.error("Failed to pin");
    }
  };

  const pinAll = async (symbols) => {
    if (!symbols || symbols.length === 0) return 0;
    if (intervals.length === 0) {
      toast.error("Select intervals first");
      return 0;
    }
    let added = 0;
    let nextPinned = [...pinned];
    // Filter out already-pinned + respect cap
    const toAdd = symbols.filter((s) => !nextPinned.some((p) => p.symbol === s));
    for (const sym of toAdd) {
      if (nextPinned.length >= MAX_PIN) {
        toast.info(`Compare cap reached (${MAX_PIN}). Skipped ${toAdd.length - added} more.`);
        break;
      }
      try {
        const data = await computeTotalReturn(sym, intervals);
        nextPinned = [...nextPinned, { symbol: data.symbol, name: data.name, results: data.results }];
        added += 1;
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.warn(`[compare] pin failed for ${sym}:`, err);
        }
        // Skip failures silently — surface count at the end via toast in caller
      }
    }
    setPinned(nextPinned);
    return added;
  };

  // When intervals change, refresh pinned compare data so the table stays in sync
  useEffect(() => {
    if (pinned.length === 0) return;
    let cancelled = false;
    (async () => {
      const updated = await Promise.all(
        pinned.map(async (p) => {
          try {
            const d = await computeTotalReturn(p.symbol, intervals);
            return { symbol: d.symbol, name: d.name, results: d.results };
          } catch (err) {
            if (process.env.NODE_ENV === "development") {
              console.warn(`[compare] refresh failed for ${p.symbol}:`, err);
            }
            return p;
          }
        })
      );
      if (!cancelled) setPinned(updated);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervals.join(",")]);

  const clearInput = () => {
    setQuery("");
    setSuggestions([]);
    setActiveSymbol(null);
    setQuote(null);
    setEtf(null);
    setEtfYtdReturn(null);
    setResults(null);
    inputRef.current?.focus();
  };

  return (
    <div className="relative min-h-screen text-zinc-100">
      <TickerHeader />

      <main className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* HERO */}
        <section className="mb-8 sm:mb-10" data-testid="hero-section">
          <div className="flex items-center gap-2 text-label mb-3">
            <span className="live-dot inline-block" />
            <span>TOTAL RETURN ANALYTICS · LIVE</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight max-w-3xl">
            Price any US stock or ETF<br />
            <span className="text-zinc-500">across the intervals that matter.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-zinc-400">
            Built for income & covered-call ETF investors. Pulls live quotes from Finnhub,
            historical prices and dividend events filtered from{" "}
            <span className="font-mono text-zinc-200">2020-01-01</span> onward. Toggle{" "}
            <span className="font-mono text-zinc-200">Simple TR</span> vs{" "}
            <span className="font-mono text-zinc-200">Reinvested (DRIP)</span>, pin up to 4 tickers to compare.
          </p>
        </section>

        {/* SEARCH */}
        <section ref={wrapRef} className="relative mb-8" data-testid="search-section">
          <div className="text-label mb-2">01 · Ticker</div>
          <div className="relative border border-zinc-800 bg-[#0d0d0f] focus-within:border-zinc-500 transition-colors">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              ref={inputRef}
              data-testid="ticker-search-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSugg(true);
              }}
              onFocus={() => setShowSugg(true)}
              placeholder="Search: QQQI, SPYI, JEPI, JEPQ, GDXY…"
              className="font-mono w-full h-14 sm:h-16 bg-transparent pl-11 pr-12 text-base sm:text-lg outline-none placeholder:text-zinc-600 tracking-wide"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {searching && <Loader2 size={14} className="animate-spin text-zinc-500" />}
              {query && (
                <button
                  data-testid="clear-search-btn"
                  onClick={clearInput}
                  className="text-zinc-500 hover:text-zinc-200 transition-colors p-1"
                  aria-label="Clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {showSugg && suggestions.length > 0 && (
            <div
              data-testid="search-suggestions"
              className="absolute left-0 right-0 mt-1 z-50 border border-zinc-800 bg-[#0d0d0f] max-h-80 overflow-auto animate-fade-in-up"
            >
              {suggestions.map((s, i) => (
                <button
                  key={`${s.symbol}-${i}`}
                  data-testid={`suggestion-${s.symbol}`}
                  onClick={() => loadSymbol(s.symbol)}
                  className="w-full text-left px-4 py-3 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/60 transition-colors flex items-center gap-3"
                >
                  <span className="font-mono text-sm text-zinc-100 min-w-[80px]">
                    {s.display_symbol}
                  </span>
                  <span className="text-sm text-zinc-400 truncate flex-1">
                    {s.description}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                    {s.type}
                  </span>
                </button>
              ))}
            </div>
          )}

          {showSugg && !searching && debounced && suggestions.length === 0 && !searchError && (
            <div className="absolute left-0 right-0 mt-1 z-50 border border-zinc-800 bg-[#0d0d0f] px-4 py-3 text-sm text-zinc-500">
              No matches for "{debounced}"
            </div>
          )}

          {showSugg && !searching && searchError && (
            <div
              data-testid="search-error"
              role="alert"
              className="absolute left-0 right-0 mt-1 z-50 border border-[#FF3B30]/40 bg-[#0d0d0f] px-4 py-3 text-sm text-[#FF3B30] flex items-center justify-between gap-3"
            >
              <span>{searchError}</span>
              <button
                data-testid="search-retry-btn"
                onClick={() => {
                  // Force a re-fetch by nudging the debounced query
                  const cur = query;
                  setSearchError(null);
                  setQuery("");
                  setTimeout(() => setQuery(cur), 0);
                }}
                className="font-mono text-[10px] uppercase tracking-widest border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 px-2.5 py-1 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          <div className="mt-2 text-xs text-zinc-600">
            Try:{" "}
            <button onClick={() => { setQuery("QQQI"); loadSymbol("QQQI"); }} className="font-mono text-zinc-300 hover:text-white underline-offset-2 hover:underline mr-3" data-testid="quick-qqqi">QQQI</button>
            <button onClick={() => { setQuery("SPYI"); loadSymbol("SPYI"); }} className="font-mono text-zinc-300 hover:text-white underline-offset-2 hover:underline mr-3" data-testid="quick-spyi">SPYI</button>
            <button onClick={() => { setQuery("JEPQ"); loadSymbol("JEPQ"); }} className="font-mono text-zinc-300 hover:text-white underline-offset-2 hover:underline mr-3" data-testid="quick-jepq">JEPQ</button>
            <button onClick={() => { setQuery("JEPI"); loadSymbol("JEPI"); }} className="font-mono text-zinc-300 hover:text-white underline-offset-2 hover:underline" data-testid="quick-jepi">JEPI</button>
          </div>
        </section>

        {/* SUMMARY GRID */}
        {(loadingQuote || quote) && (
          <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up" data-testid="summary-grid">
            <div className="md:col-span-2">
              <StockSummaryCard
                quote={quote}
                loading={loadingQuote}
                pinned={!!isPinned}
                onPin={togglePin}
              />
            </div>
            <div>
              <DividendInfoCard quote={quote} loading={loadingQuote} />
            </div>
          </section>
        )}

        {/* ETF DETAIL GRID (only renders for ETFs) */}
        {quote && etf && etf.is_etf && (
          <section
            className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up"
            data-testid="etf-grid"
          >
            <div className="md:col-span-2">
              <EtfFundamentalsCard
                etf={etf}
                loading={false}
                ytdTotalReturnPct={etfYtdReturn}
                onTypeClick={(t) => setDrawerTypeId(t.id)}
              />
            </div>
            <div>
              <EtfHoldingsCard etf={etf} loading={false} />
            </div>
          </section>
        )}
        {quote && loadingEtf && !etf && (
          <section
            className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in-up"
            data-testid="etf-grid-loading"
          >
            <div className="md:col-span-2">
              <EtfFundamentalsCard etf={null} loading={true} />
            </div>
            <div>
              <EtfHoldingsCard etf={null} loading={true} />
            </div>
          </section>
        )}

        {/* Held by ETFs (only for stocks) */}
        {quote && etf && etf.is_etf === false && (
          <section className="mb-8 animate-fade-in-up">
            <HeldByEtfsCard
              symbol={quote.symbol}
              isEtf={false}
              onPickTicker={(sym) => loadSymbol(sym)}
            />
          </section>
        )}

        {/* Similar ETFs (only for ETFs) */}
        {quote && etf && etf.is_etf && (
          <section className="mb-8 animate-fade-in-up">
            <PeerEtfsStrip
              symbol={quote.symbol}
              isEtf={true}
              onPickTicker={(sym) => loadSymbol(sym)}
              onPinAll={(symbols) => pinAll(symbols)}
            />
          </section>
        )}

        {/* INTERVALS */}
        {quote && (
          <section className="mb-8 animate-fade-in-up" data-testid="intervals-section">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-label">02 · Intervals (max 4)</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                {intervals.length}/4 selected
              </div>
            </div>
            <IntervalSelector value={intervals} onChange={setIntervals} ipoDate={quote?.ipo_date} />
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <button
                data-testid="calculate-return-button"
                onClick={onCompute}
                disabled={computing || intervals.length === 0}
                className="font-mono uppercase tracking-widest text-xs bg-white text-black px-6 h-11 hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
              >
                {computing ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Calculating
                  </>
                ) : (
                  <>
                    <TrendingUp size={14} /> Calculate Total Return
                  </>
                )}
              </button>
              <span className="text-xs text-zinc-500">
                Switch to <span className="font-mono text-zinc-300">Reinvested</span> after results load to see DRIP effect.
              </span>
            </div>
          </section>
        )}

        {/* RESULTS */}
        {(computing || results) && (
          <section className="mb-12 animate-fade-in-up" data-testid="results-section">
            <div className="text-label mb-3">03 · Results</div>
            <ResultsPanel
              data={results}
              loading={computing}
              currency={quote?.currency}
              mode={mode}
              onChangeMode={setMode}
            />
          </section>
        )}

        {/* COMPARE */}
        {pinned.length > 0 && (
          <section data-testid="compare-section">
            <div className="text-label mb-3 flex items-center gap-2">
              <Layers size={12} /> 04 · Compare
            </div>
            <CompareView
              rows={pinned}
              intervals={intervals}
              mode={mode}
              onRemove={(sym) => setPinned((p) => p.filter((x) => x.symbol !== sym))}
              onClearAll={() => setPinned([])}
              onSaveAsWatchlist={
                user
                  ? async () => {
                      const name = window.prompt(
                        "Name this watchlist:",
                        pinned.map((p) => p.symbol).join(" · ")
                      );
                      if (!name) return;
                      try {
                        await createWatchlist(
                          name.trim(),
                          pinned.map((p) => p.symbol)
                        );
                        toast.success(`Saved "${name.trim()}" — ${pinned.length} tickers`);
                        // Bump a global event so WatchlistsPanel reloads
                        window.dispatchEvent(new CustomEvent("watchlists:reload"));
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Save failed");
                      }
                    }
                  : undefined
              }
            />
          </section>
        )}

        {/* WATCHLISTS (logged-in only) */}
        {user && (
          <WatchlistsPanel
            activeSymbol={activeSymbol}
            onPickTicker={(sym) => loadSymbol(sym)}
            onPinAll={pinAll}
          />
        )}

        {/* EMPTY STATE */}
        {!quote && !loadingQuote && (
          <section className="border border-dashed border-zinc-800 p-10 sm:p-16 text-center" data-testid="empty-state">
            <Activity className="mx-auto mb-4 text-zinc-700" size={28} />
            <div className="text-label mb-2">No ticker loaded</div>
            <div className="text-zinc-500 text-sm">
              Search a US ticker above to load live price, dividend data, and total return calculator.
            </div>
          </section>
        )}
      </main>

      <footer className="relative z-10 border-t border-zinc-900 mt-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="font-mono uppercase tracking-widest">
            Quote: Finnhub · History & dividends: Yahoo Finance · Data ≥ 2020-01-01
          </div>
          <div className="font-mono flex flex-col sm:items-end gap-1">
            <div>
              Powered by{" "}
              <a
                data-testid="dividend-community-link"
                href="https://www.dividend.community"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-300 hover:text-white underline-offset-4 hover:underline transition-colors"
              >
                Dividend Community
              </a>
            </div>
            <div>© {new Date().getFullYear()} · TRT</div>
          </div>
        </div>
      </footer>

      {/* "All ETFs of type X by AUM" side-panel — opens when an EtfTypeChip is clicked */}
      <EtfsByTypeDrawer
        typeId={drawerTypeId}
        onClose={() => setDrawerTypeId(null)}
        onPickTicker={(sym) => loadSymbol(sym)}
      />
    </div>
  );
}
